// DrJobs Admin Panel — Configuration
// Replaces legacy Rodeo Picks project configuration

export const APPLICATION_NAME = "DrJobs Admin";
export const APPLICATION_GLOBAL_SLUG = "drjobs-admin";
export const APPLICATION_VERSION = "1.0.0";
export const STORAGE = "DRJOBS-ADMIN";
export const SELECTED_EVENT_STORAGE = ""; // kept for callApi.js compatibility (no-op remove)
export const PAGE_TITLE = `${APPLICATION_NAME} - `;
export const PER_PAGE = 20;

// Required by functions.ts (imported from ".")
export const CHAT_DATE_TIME_FORMAT = {
  DISPLAY_DATE_WITH_TIME: "Do MMMM, YYYY hh:mm a",
  DISPLAY_DAY_TIME: "dddd hh:mm a",
};

// API base URL — set VITE_API_BASE_URL in .env, defaults to local Laravel dev server
const BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ?? "http://127.0.0.1:8000/api/v1";

// Employer portal URL — set VITE_EMPLOYER_PORTAL_URL in .env
export const EMPLOYER_PORTAL_URL =
  (import.meta as any).env?.VITE_EMPLOYER_PORTAL_URL ?? "https://www.drjobpro.com";

//#region URLS section

export const URLS = {
  HOME: "/",
  AUTH: {
    LOGIN:      "/login",
    VERIFY_OTP: "/verify-otp",
  },
  DASHBOARD: {
    HOME: "/",
  },
};

//#region API_URL section

export const API_URL = {
  AUTH: {
    LOGIN:           `${BASE}/admin/login`,
    LOGOUT:          `${BASE}/admin/logout`,
    PROFILE:         `${BASE}/admin/profile`,
    DETAILS:         `${BASE}/admin/profile`, // alias used by index.tsx startup check
    UPDATE_PROFILE:  `${BASE}/admin/update-profile`,
    CHANGE_PASSWORD: `${BASE}/admin/change-password`,
    TOGGLE_2FA:      `${BASE}/admin/update-2fa`,
    VERIFY_OTP:      `${BASE}/admin/verify-otp`,
    RESEND_OTP:      `${BASE}/admin/resend-otp`,
    UPLOAD_IMAGE:    `${BASE}/admin/upload-profile-image`,
  },
  DASHBOARD: {
    GET:               `${BASE}/admin/reports/dashboard`,
    JOB_STATUS_COUNTS: `${BASE}/admin/reports/job-status-counts`,
  },
  COMPANIES: {
    LIST:          `${BASE}/admin/companies`,
    DETAILS:       (id: number | string) => `${BASE}/admin/companies/${id}/details`,
    SUBSCRIPTIONS: (id: number | string) => `${BASE}/admin/company-subscriptions/${id}`,
    UPDATE_STATUS: `${BASE}/admin/update-user-status`,
    UPDATE_VERIFY: `${BASE}/admin/update-user-verify`,
    IMPERSONATE:   (id: number | string) => `${BASE}/admin/companies/${id}/impersonate`,
    QUOTA_OVERRIDE:(id: number | string) => `${BASE}/admin/companies/${id}/quota-override`,
    ACTIVITIES:    `${BASE}/admin/user-activities`,
  },
  ANALYTICS: {
    REVENUE:          `${BASE}/admin/analytics/revenue`,
    SUBSCRIPTION:     `${BASE}/admin/analytics/subscription`,
    GROWTH:           `${BASE}/admin/analytics/growth`,
    FUNNEL:           `${BASE}/admin/analytics/funnel`,
    MEETINGS:         `${BASE}/admin/analytics/meetings`,
    PERFORMANCE:      `${BASE}/admin/analytics/performance`,
    AI_USAGE:         `${BASE}/admin/analytics/ai-usage`,
    PAYMENT_DETAILS:  `${BASE}/admin/payment-details`,
    INVOICES:              `${BASE}/admin/analytics/invoices`,
    INVOICES_DOWNLOAD_ALL: `${BASE}/admin/analytics/invoices/download-all`,
    INVOICE_DOWNLOAD:      (paymentId: string) => `${BASE}/admin/invoice/${paymentId}/download`,
  },
  SALES_NOTES: {
    GET:    (companyId: number | string) => `${BASE}/admin/sales-notes/${companyId}`,
    CREATE: `${BASE}/admin/sales-notes`,
    UPDATE: (noteId: number | string) => `${BASE}/admin/sales-notes/${noteId}`,
    DELETE: (noteId: number | string) => `${BASE}/admin/sales-notes/${noteId}`,
  },
  JOBS: {
    LIST:         `${BASE}/admin/job-moderation/list`,
    BULK_STATUS:  `${BASE}/admin/job-moderation/bulk-status`,
    COMPANY_JOBS: (companyId: number | string) => `${BASE}/admin/jobs/company/${companyId}`,
  },
  ADMIN_USERS: {
    LIST:       `${BASE}/admin/admin-users/list`,
    CREATE:     `${BASE}/admin/admin-users/create`,
    UPDATE:     (id: string) => `${BASE}/admin/admin-users/${id}`,
    DEACTIVATE: (id: string) => `${BASE}/admin/admin-users/${id}/deactivate`,
  },
  SUPPORT: {
    LIST:          `${BASE}/admin/contact-support/list`,
    GET:           (id: string) => `${BASE}/admin/contact-support/${id}`,
    UPDATE_STATUS: (id: string) => `${BASE}/admin/contact-support/${id}/status`,
    REPLY:         (id: string) => `${BASE}/admin/contact-support/${id}/reply`,
    RATINGS_LIST:        `${BASE}/admin/experience-ratings`,
    RATINGS_REPLY:       (id: string) => `${BASE}/admin/experience-ratings/${id}/reply`,
    RATINGS_MARK_REVIEW: (id: string) => `${BASE}/admin/experience-ratings/${id}/mark-review`,
  },
  SETTINGS: {
    LIST:   `${BASE}/admin/settings`,
    CREATE: `${BASE}/admin/settings`,
    UPDATE: (id: string) => `${BASE}/admin/settings/${id}`,
    DELETE: (id: string) => `${BASE}/admin/settings/${id}`,
  },
  EMAIL_TEMPLATES: {
    LIST:   `${BASE}/admin/email-templates`,
    GET:    (id: string | number) => `${BASE}/admin/email-templates/${id}`,
    UPDATE: (id: string | number) => `${BASE}/admin/email-templates/${id}`,
  },
  SUBSCRIPTION_PLANS: {
    LIST:   `${BASE}/admin/subscription-plans/list`,
    GET:    `${BASE}/admin/subscription-plans/get`,
    UPDATE: `${BASE}/admin/subscription-plans/update`,
  },
};

//#region ACTIONS section

export const ACTIONS = {
  LOADER: {
    SET_FP_STATUS: "DRJOBS_ADMIN/LOADER/SET_FP_STATUS",
  },
  AUTH: {
    LOGIN:              "DRJOBS_ADMIN/AUTH/LOGIN",
    LOGOUT:             "DRJOBS_ADMIN/AUTH/LOGOUT",
    ME:                 "DRJOBS_ADMIN/AUTH/ME",
    UPDATE_PROFILE:     "DRJOBS_ADMIN/AUTH/UPDATE_PROFILE",
    UPDATE_DISPLAY_NAME:"DRJOBS_ADMIN/AUTH/UPDATE_DISPLAY_NAME",
    SET_GUEST_USER_ID:  "DRJOBS_ADMIN/AUTH/SET_GUEST_USER_ID",
    UPDATE_DOB:         "DRJOBS_ADMIN/AUTH/UPDATE_DOB",
    UPDATE_EMAIL:       "DRJOBS_ADMIN/AUTH/UPDATE_EMAIL",
    SET_PICK_IDS:       "DRJOBS_ADMIN/AUTH/SET_PICK_IDS",
  },
};

//#region SAGA_ACTIONS section

export const SAGA_ACTIONS = {
  ADMIN: {
    AUTH: {
      LOGIN:           "SAGA/ADMIN/AUTH/LOGIN",
      LOGOUT:          "SAGA/ADMIN/AUTH/LOGOUT",
      GET_PROFILE:     "SAGA/ADMIN/AUTH/GET_PROFILE",
      UPDATE_PROFILE:  "SAGA/ADMIN/AUTH/UPDATE_PROFILE",
      CHANGE_PASSWORD: "SAGA/ADMIN/AUTH/CHANGE_PASSWORD",
      TOGGLE_2FA:      "SAGA/ADMIN/AUTH/TOGGLE_2FA",
      VERIFY_OTP:      "SAGA/ADMIN/AUTH/VERIFY_OTP",
      RESEND_OTP:      "SAGA/ADMIN/AUTH/RESEND_OTP",
    },
    DASHBOARD: {
      GET: "SAGA/ADMIN/DASHBOARD/GET",
    },
    COMPANIES: {
      LIST:          "SAGA/ADMIN/COMPANIES/LIST",
      GET_DETAILS:   "SAGA/ADMIN/COMPANIES/GET_DETAILS",
      UPDATE_STATUS: "SAGA/ADMIN/COMPANIES/UPDATE_STATUS",
      UPDATE_VERIFY: "SAGA/ADMIN/COMPANIES/UPDATE_VERIFY",
      IMPERSONATE:   "SAGA/ADMIN/COMPANIES/IMPERSONATE",
      ACTIVITIES:    "SAGA/ADMIN/COMPANIES/ACTIVITIES",
    },
    ANALYTICS: {
      REVENUE:      "SAGA/ADMIN/ANALYTICS/REVENUE",
      SUBSCRIPTION: "SAGA/ADMIN/ANALYTICS/SUBSCRIPTION",
      GROWTH:       "SAGA/ADMIN/ANALYTICS/GROWTH",
      FUNNEL:       "SAGA/ADMIN/ANALYTICS/FUNNEL",
      MEETINGS:     "SAGA/ADMIN/ANALYTICS/MEETINGS",
      PERFORMANCE:  "SAGA/ADMIN/ANALYTICS/PERFORMANCE",
      AI_USAGE:     "SAGA/ADMIN/ANALYTICS/AI_USAGE",
      INVOICES:     "SAGA/ADMIN/ANALYTICS/INVOICES",
    },
    SALES_NOTES: {
      GET:    "SAGA/ADMIN/SALES_NOTES/GET",
      CREATE: "SAGA/ADMIN/SALES_NOTES/CREATE",
      UPDATE: "SAGA/ADMIN/SALES_NOTES/UPDATE",
      DELETE: "SAGA/ADMIN/SALES_NOTES/DELETE",
    },
    JOBS: {
      LIST:         "SAGA/ADMIN/JOBS/LIST",
      BULK_STATUS:  "SAGA/ADMIN/JOBS/BULK_STATUS",
      COMPANY_JOBS: "SAGA/ADMIN/JOBS/COMPANY_JOBS",
    },
    USERS: {
      LIST:       "SAGA/ADMIN/USERS/LIST",
      CREATE:     "SAGA/ADMIN/USERS/CREATE",
      UPDATE:     "SAGA/ADMIN/USERS/UPDATE",
      DEACTIVATE: "SAGA/ADMIN/USERS/DEACTIVATE",
    },
    SUPPORT: {
      LIST:          "SAGA/ADMIN/SUPPORT/LIST",
      GET:           "SAGA/ADMIN/SUPPORT/GET",
      UPDATE_STATUS: "SAGA/ADMIN/SUPPORT/UPDATE_STATUS",
      REPLY:         "SAGA/ADMIN/SUPPORT/REPLY",
      RATINGS_LIST:        "SAGA/ADMIN/SUPPORT/RATINGS_LIST",
      RATINGS_REPLY:       "SAGA/ADMIN/SUPPORT/RATINGS_REPLY",
      RATINGS_MARK_REVIEW: "SAGA/ADMIN/SUPPORT/RATINGS_MARK_REVIEW",
    },
    SETTINGS: {
      LIST:   "SAGA/ADMIN/SETTINGS/LIST",
      CREATE: "SAGA/ADMIN/SETTINGS/CREATE",
      UPDATE: "SAGA/ADMIN/SETTINGS/UPDATE",
      DELETE: "SAGA/ADMIN/SETTINGS/DELETE",
    },
    EMAIL_TEMPLATES: {
      LIST:   "SAGA/ADMIN/EMAIL_TEMPLATES/LIST",
      GET:    "SAGA/ADMIN/EMAIL_TEMPLATES/GET",
      UPDATE: "SAGA/ADMIN/EMAIL_TEMPLATES/UPDATE",
    },
    SUBSCRIPTION_PLANS: {
      LIST:   "SAGA/ADMIN/SUBSCRIPTION_PLANS/LIST",
      UPDATE: "SAGA/ADMIN/SUBSCRIPTION_PLANS/UPDATE",
    },
  },
};

// Re-export utility functions (sagaFunction, createApiCaller, extractErrorMessage, etc.)
export * from "./functions";
